'use client';

import React from 'react';
import {
  Phone,
  FileSignature,
  Building2,
  LayoutDashboard,
  Check,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type FooterKind = { type: 'clock'; badgeKey: string } | { type: 'dot'; badgeKey: string; dotClass: string };

type StepConfig = {
  id: string;
  num: string;
  icon: LucideIcon;
  titleKey: string;
  leadKey: string;
  descKey?: string;
  checkKeys?: readonly [string, string];
  introKey?: string;
  bulletKeys?: readonly [string, string, string];
  footer: FooterKind | { type: 'none' };
  lightColor: string;
  textColor: string;
};

const STEPS: StepConfig[] = [
  {
    id: 'step-1',
    num: '01',
    icon: Phone,
    titleKey: 'how.step1.title',
    leadKey: 'how.step1.lead',
    descKey: 'how.step1.desc',
    footer: { type: 'clock', badgeKey: 'how.step1.badge' },
    // Solid-tint chip so the phone icon matches other steps (8% navy was nearly invisible on white)
    lightColor: 'bg-[#E8EDF4]',
    textColor: 'text-[#1B3A5C]',
  },
  {
    id: 'step-2',
    num: '02',
    icon: FileSignature,
    titleKey: 'how.step2.title',
    leadKey: 'how.step2.lead',
    descKey: 'how.step2.desc',
    checkKeys: ['how.step2.check1', 'how.step2.check2'],
    footer: { type: 'none' },
    lightColor: 'bg-[#C9A96E]/10',
    textColor: 'text-[#8a6830]',
  },
  {
    id: 'step-3',
    num: '03',
    icon: Building2,
    titleKey: 'how.step3.title',
    leadKey: 'how.step3.lead',
    introKey: 'how.step3.intro',
    bulletKeys: ['how.step3.bullet1', 'how.step3.bullet2', 'how.step3.bullet3'],
    footer: { type: 'clock', badgeKey: 'how.step3.badge' },
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
  },
  {
    id: 'step-4',
    num: '04',
    icon: LayoutDashboard,
    titleKey: 'how.step4.title',
    leadKey: 'how.step4.lead',
    descKey: 'how.step4.desc',
    bulletKeys: ['how.step4.bullet1', 'how.step4.bullet2', 'how.step4.bullet3'],
    footer: { type: 'dot', badgeKey: 'how.step4.badge', dotClass: 'bg-emerald-500' },
    lightColor: 'bg-slate-100',
    textColor: 'text-slate-700',
  },
];

export default function HowItWorksSection() {
  const { t } = useLanguage();

  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-slate-50 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-3xl">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('how.tag')}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1B3A5C] tracking-tight">
            {t('how.title')}
          </h2>
          <div className="flex items-start gap-2.5 text-sm text-slate-500 mt-6">
            <Clock size={16} className="text-[#C9A96E] shrink-0 mt-0.5" />
            <p className="leading-relaxed">{t('how.desc')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id}>
                <div className="bg-white rounded-2xl border border-slate-200 p-6 h-full flex flex-col hover:border-[#1B3A5C]/25 hover:shadow-lg transition-all duration-200 group">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold text-slate-300 tracking-widest">{step.num}</span>
                    <div className={`w-10 h-10 rounded-xl ${step.lightColor} flex items-center justify-center`}>
                      <Icon size={18} className={step.textColor} />
                    </div>
                  </div>

                  <h3 className="font-display font-bold text-base text-[#1B3A5C] mb-2">{t(step.titleKey)}</h3>
                  <p className="text-sm font-medium text-slate-700 leading-snug mb-2">{t(step.leadKey)}</p>

                  {step.descKey ? (
                    <p className="text-sm text-slate-500 leading-relaxed mb-3">{t(step.descKey)}</p>
                  ) : null}

                  {step.checkKeys ? (
                    <ul className={`space-y-2 mb-1 ${step.footer.type === 'none' ? 'mt-auto' : ''}`}>
                      {step.checkKeys.map((key) => (
                        <li key={key} className="flex gap-2 text-sm text-slate-600">
                          <Check className="size-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2.5} aria-hidden />
                          <span>{t(key)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {step.introKey && step.bulletKeys ? (
                    <>
                      <p className="text-sm text-slate-600 mt-1 mb-2">{t(step.introKey)}</p>
                      <ul className="space-y-2 mb-3">
                        {step.bulletKeys.map((key) => (
                          <li key={key} className="flex gap-2 text-sm text-slate-600">
                            <Check
                              className={`size-4 shrink-0 mt-0.5 ${step.id === 'step-3' ? 'text-[#1B3A5C]' : 'text-emerald-600'}`}
                              strokeWidth={2.5}
                              aria-hidden
                            />
                            <span>{t(key)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : step.bulletKeys && step.descKey ? (
                    <ul className="space-y-2 mb-3 mt-1">
                      {step.bulletKeys.map((key) => (
                        <li key={key} className="flex gap-2 text-sm text-slate-600">
                          <Check className="size-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2.5} aria-hidden />
                          <span>{t(key)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {step.footer.type !== 'none' ? (
                    <div className="mt-auto pt-4">
                      {step.footer.type === 'clock' ? (
                        <div
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${step.lightColor} text-xs font-semibold ${step.textColor}`}
                        >
                          <Clock size={13} className="shrink-0 opacity-90" aria-hidden />
                          {t(step.footer.badgeKey)}
                        </div>
                      ) : (
                        <div
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${step.lightColor} text-xs font-semibold ${step.textColor}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${step.footer.dotClass}`} />
                          {t(step.footer.badgeKey)}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <a href="#contact" className="btn-primary px-10 py-3.5 text-base font-semibold shadow-md shadow-[#1B3A5C]/15">
            {t('how.cta')}
          </a>
          <a href="#services" className="text-sm text-slate-500 hover:text-[#1B3A5C] transition-colors">
            {t('how.learnMore')}
          </a>
        </div>
      </div>
    </section>
  );
}
